import classNames from 'classnames'
import { forwardRef } from 'react'

import classes from './Slider.module.scss'
import type { SliderCSSProperties, SliderProps } from './types'

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value))

/** Decimal places of a number, including ones written in exponent form. */
const decimalPlacesOf = (value: number) => {
  const [mantissa, exponent = '0'] = String(value).split('e')

  return Math.max(0, (mantissa.split('.')[1] ?? '').length - Number(exponent))
}

/**
 * The position the rail can show for a value: clamped to the rail, then
 * rounded to the nearest step counted from `min` (ties upwards), never past
 * `max`. Mirrors how browsers sanitize a range input's value, so the thumb,
 * the fill and the value reported back always agree.
 */
const snapToStep = (value: number, min: number, max: number, step: number) => {
  const clamped = clamp(value, min, max)

  if (!(step > 0) || !Number.isFinite(step)) return clamped

  const precision = Math.max(decimalPlacesOf(min), decimalPlacesOf(step))
  const toGrid = (steps: number) =>
    Number((min + steps * step).toFixed(precision))

  // Trims float noise first, so 0.35 / 0.1 still counts as the tie it is.
  const steps = Math.round(Number(((clamped - min) / step).toPrecision(12)))
  const snapped = toGrid(steps)

  return snapped > max ? toGrid(steps - 1) : snapped
}

/** Keys a focused range input moves its value on. */
const VALUE_KEYS = new Set([
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'PageUp',
  'PageDown',
  'Home',
  'End'
])

const toFillPercentage = (value: number, min: number, max: number) =>
  max > min ? ((value - min) / (max - min)) * 100 : 0

const defaultFormatValue = (value: number) => String(value)

export const Slider = forwardRef<HTMLInputElement, SliderProps>(
  (props, ref) => {
    const {
      value,
      min = 0,
      max = 100,
      step = 1,
      onChange,
      onValueCommitted,
      formatValue = defaultFormatValue,
      showScale,
      isDisabled,
      isError,
      readOnly,
      className,
      style,
      children,
      ...rest
    } = props

    const displayedValue = snapToStep(value, min, max, step)

    /**
     * A value off the rail's grid (typed into a companion input, say) shows
     * at its nearest position. Interacting adopts that position first, so
     * landing on it still reaches `onChange` (the browser fires no change
     * for its own position) and every further move starts from the grid.
     */
    const adoptDisplayedValue = () => {
      if (readOnly || isDisabled) return
      if (displayedValue === value || !Number.isFinite(displayedValue)) return

      onChange?.(displayedValue)
    }

    const fillStyle: SliderCSSProperties = {
      ...style,
      '--fill': `${toFillPercentage(displayedValue, min, max)}%`
    } as SliderCSSProperties

    return (
      <div
        className={classNames(
          'Concorde-Slider',
          classes.root,
          isDisabled && classes['root--disabled'],
          isError && classes['root--error'],
          readOnly && classes['root--readOnly'],
          className
        )}
        style={fillStyle}
      >
        <input
          aria-invalid={isError || undefined}
          aria-readonly={readOnly || undefined}
          aria-valuetext={formatValue(displayedValue)}
          {...rest}
          className={classNames('Concorde-Slider__Input', classes.input)}
          disabled={isDisabled}
          max={max}
          min={min}
          onChange={(event) => {
            if (readOnly) return

            onChange?.(event.target.valueAsNumber)
          }}
          onKeyDown={(event) => {
            if (VALUE_KEYS.has(event.key)) adoptDisplayedValue()
            rest.onKeyDown?.(event)
          }}
          onKeyUp={(event) => {
            rest.onKeyUp?.(event)
            onValueCommitted?.(event.currentTarget.valueAsNumber)
          }}
          onPointerDown={(event) => {
            adoptDisplayedValue()
            rest.onPointerDown?.(event)
          }}
          onPointerUp={(event) => {
            rest.onPointerUp?.(event)
            onValueCommitted?.(event.currentTarget.valueAsNumber)
          }}
          ref={ref}
          step={step}
          type="range"
          value={displayedValue}
        />
        {showScale && (
          <div
            aria-hidden="true"
            className={classNames('Concorde-Slider__Scale', classes.scale)}
          >
            <span>{formatValue(min)}</span>
            <span>{formatValue(max)}</span>
          </div>
        )}
        {children}
      </div>
    )
  }
)

Slider.displayName = 'Slider'
